import { getSidebarItem, sidebarConfig, SidebarItemKey } from "../../../config/sidebarConfig";
import type { AnswerEntry } from "./types";

/** "take me to circles" names a sidebar page. "take me to it" and "where is Circles?" do not. */
const OPEN_PAGE =
  /^(?:please\s+)?(?:take me to|go to|navigate to)(?:\s+the)?\s+(.+?)(?:\s+page)?[?.!]*$/i;

/** "open users": only a page's own name, so "open the notice" or "open my draft" stays what it was. */
const OPEN_NAMED_PAGE = /^(?:please\s+)?open(?:\s+the)?\s+(.+?)(?:\s+page)?[?.!]*$/i;

export function pageToOpen(text: string): { id: string; key: SidebarItemKey; label: string; href: string } | null {
  const opened = OPEN_NAMED_PAGE.exec(text.trim());
  if (opened) {
    const said = opened[1].trim().toLowerCase();
    const item = sidebarConfig.navItems.find((entry) => entry.label.toLowerCase() === said);
    return item ? { id: `${item.key}.open`, key: item.key, label: item.label, href: item.path } : null;
  }
  const match = OPEN_PAGE.exec(text.trim());
  if (!match) return null;
  let name = match[1].replace(/^(?:my|our)\s+/i, "").trim().toLowerCase();
  if (/^(?:it|that|this|them|those|one)$/.test(name)) return null;
  if (name === "calender") name = "calendar";
  if (name === "creat" || name === "crate" || name === "craete") name = "create";
  if (name === "notcies") name = "notices";
  if (name === "circle") name = "circles";
  if (name === "notice") name = "notices";
  if (name === "help") name = "help center";
  const item = sidebarConfig.navItems.find(
    (entry) => entry.label.toLowerCase() === name || entry.key.toLowerCase() === name
  );
  return item ? { id: `${item.key}.open`, key: item.key, label: item.label, href: item.path } : null;
}

/**
 * "Where is…?" for a sidebar page: says what the page is for and links to it.
 * `about` is the page's own description, in its on-screen words.
 */
export function openPageEntry(
  sidebarItem: SidebarItemKey,
  about: string,
  examples: string[]
): AnswerEntry {
  const { label, path } = getSidebarItem(sidebarItem);
  return {
    id: `${sidebarItem}.open`,
    sidebarItem,
    examples: [
      ...examples,
      `Open ${label}`,
      `Go to ${label}`,
      `Take me to ${label}`,
      `Where is ${label}?`,
      `Show me ${label}`,
      `Open the ${label} page`,
      `Where can I find ${label}?`,
      `How do I get to ${label}?`,
      `Navigate to ${label}`,
      `I need the ${label} page`,
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => `You'll find that on ${label}. ${about}`,
        () => `That's on ${label}, in the sidebar. ${about}`,
        () => `${label} is the page you want. ${about}`,
      ],
      link: () => ({ label: `Open ${label}`, href: path }),
    },
  };
}
